import { commercialReference } from './quoteReference';
import { assertEuro } from './quoteFinancialInput';
import { assertFinancialPresentation, taxBreakdownHtml } from './quoteFinancialPresentation';
import {
  DOCUMENT_COMPOSITIONS,
  buildHtmlDocumentShell,
  companyDocumentIdentity,
  openHtmlDocument,
} from '../../shared/documents';
import quoteCoverLogo from '../../assets/icc-logo-vertical-color.png';
import {
  clientPaymentScheduleItems,
  deliveryFormatLabel,
  formatCodeLabel,
  formatPaymentScheduleItem,
  isTechnicalPlanningImportLabel,
  quoteParameterText,
} from './quoteContractTerms';
import { formatQuantity } from './quoteNumberFormat';
import { clientOptionDocumentViews } from './quoteClientOptions';
import { assertQuoteDocumentReady } from './quoteDocumentReadiness';
import { commercialFields, projectClientSections } from './quoteClientDocument';
import { documentTerms } from './quoteTermsSnapshot';
import { documentV2RenderContext } from './quoteDocumentV2';
import { previewQuoteDocument } from './quote.api';

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const formatDate = (value = new Date()) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR").format(date);
};

const formatPercentage = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0';

  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const getValue = (source, ...keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null && source?.[key] !== "") {
      return source[key];
    }
  }

  return "";
};

const getProjectClient = (project) => project?.Client || project?.client || project?.company || null;

const getQuoteClient = (editor, project) => {
  const snapshot = editor?.customerSnapshot || {};
  if (snapshot.customerLegalName || snapshot.customerAddressLine1 || snapshot.customerContactName) {
    return {
      name: snapshot.customerLegalName || snapshot.customerTradeName,
      socialReason: snapshot.customerLegalName,
      addressLine1: snapshot.customerAddressLine1,
      addressLine2: snapshot.customerAddressLine2,
      postalCode: snapshot.customerPostalCode,
      city: snapshot.customerCity,
      country: snapshot.customerCountry,
      email: snapshot.customerContactEmail,
      phone: snapshot.customerContactPhone,
      contactName: snapshot.customerContactName,
    };
  }
  return getProjectClient(project);
};

const getClientName = (client, project) =>
  getValue(client, "socialReason", "social_reason", "legalName", "legal_name", "name") ||
  getValue(project, "clientName", "client_name") ||
  "Client a renseigner";

const formatClientAddress = (client, project) => [
  getValue(client, "addressLine1", "address_line1", "address"),
  getValue(client, "addressLine2", "address_line2"),
  [
    getValue(client, "postalCode", "postal_code"),
    getValue(client, "city"),
  ].filter(Boolean).join(" "),
  getValue(client, "country", "country_code"),
].filter(Boolean);

const getClientAddressHtml = (client, project) => {
  const addressLines = formatClientAddress(client, project);

  if (addressLines.length) {
    return addressLines.map(escapeHtml).join("<br />");
  }

  return escapeHtml(getValue(project, "address_line1", "addressLine1", "address") || "Adresse client a renseigner");
};

const getLineUnitPrice = (line) => {
  if (line.unitPriceHT != null) return Number(line.unitPriceHT);
  const totalHT = Number(line.sellingPriceHT ?? line.clientTotalPrice ?? 0);
  const quantity = Number(line.quantity || 0);

  if (!Number.isFinite(totalHT)) return 0;
  if (!Number.isFinite(quantity) || quantity <= 0) return totalHT;

  return totalHT / quantity;
};

const renderLineRows = (lines, formatMoney) => lines.map((line, index) => `
  <tr>
    <td class="center">${escapeHtml(line.documentNumber || index + 1)}</td>
    <td>
      <span>${line.code ? `${escapeHtml(line.code)} · ` : ''}${escapeHtml(line.description || "-")}</span>
      ${line.lineComment ? `<div class="line-comment">${escapeHtml(line.lineComment).replace(/\n/g, "<br />")}</div>` : ""}
    </td>
    <td class="center">${escapeHtml(formatQuantity(line.quantity))}</td>
    <td class="center">${escapeHtml(line.unit || "")}</td>
    <td class="right">${formatMoney(getLineUnitPrice(line))}</td>
    <td class="right">${formatMoney(line.sellingPriceHT ?? line.clientTotalPrice)}</td>
    <td class="right">${line.vatAmount == null ? 'Non disponible' : formatMoney(line.vatAmount)}</td>
    <td class="right">${line.sellingPriceTTC == null ? 'Non disponible' : formatMoney(line.sellingPriceTTC)}</td>
  </tr>
`).join("");

const sectionHeading = (section, number, depth = 0, hideTechnical = false) => `<tr class="section-row section-depth-${Math.min(depth, 4)}"><td class="section-number">${escapeHtml(number)}.</td><td colspan="7"><strong>${escapeHtml(formatCodeLabel(section.code, section.title))}</strong>${section.description && (!hideTechnical || !isTechnicalPlanningImportLabel(section.description)) ? `<div class="line-comment">${escapeHtml(section.description)}</div>` : ''}</td></tr>`;
const sectionSubtotal = (section, formatMoney) => `<tr class="section-subtotal"><td colspan="5">Sous-total</td><td class="right">${section.totalHT == null ? '—' : formatMoney(section.totalHT)}</td><td></td><td></td></tr>`;
const costingColumns = '<colgroup><col style="width:6%"><col style="width:28%"><col style="width:5%"><col style="width:5%"><col style="width:14%"><col style="width:14%"><col style="width:14%"><col style="width:14%"></colgroup>';
const costingTaxTable = (source, formatMoney) => taxBreakdownHtml(source, formatMoney).replace('</tbody>', `</tbody><tfoot><tr><td>Total</td><td>${formatMoney(source.totals.totalHT)}</td><td>${formatMoney(source.totals.totalVat)}</td><td>${formatMoney(source.totals.totalTTC)}</td></tr></tfoot>`);


const renderHierarchicalRows = (editor, calculated, formatMoney) => {
  const sections = projectClientSections(editor.sections, (calculated.clientLines || []).map((line) => ({
    id: line.id, quantity: line.quantity,
    unitPriceHT: line.sellingPriceMode === 'forced' && line.forcedUnitSellingPrice != null
      ? line.forcedUnitSellingPrice : getLineUnitPrice(line),
    totalHT: line.sellingPriceHT ?? line.clientTotalPrice, totalVat: line.vatAmount, totalTTC: line.sellingPriceTTC,
  })), calculated);
  const rows = [];
  const walk = (items, depth = 0, prefix = '') => {
    items.forEach((section, sectionIndex) => {
        const number = `${prefix}${sectionIndex + 1}`;
        const normalizedSectionTitle = String(section.title || '').trim().toLocaleLowerCase('fr-FR');
        const isDefaultQuoteLinesSection = depth === 0 && normalizedSectionTitle === 'lignes du devis';
        if (!isDefaultQuoteLinesSection) {
          rows.push(sectionHeading(section, number, depth, true));
        }
        section.lines.forEach((sourceLine, lineIndex) => {
          if (['text', 'subtotal'].includes(sourceLine.lineType)) {
            if (String(sourceLine.description || '').trim()) rows.push(`<tr class="text-row"><td></td><td colspan="7">${escapeHtml(sourceLine.description).replace(/\n/g, '<br />')}</td></tr>`);
            return;
          }
          const line = { ...sourceLine, documentNumber: isDefaultQuoteLinesSection ? String(lineIndex + 1) : `${number}.${lineIndex + 1}`, sellingPriceHT: sourceLine.totalHT,
            vatAmount: sourceLine.totalVat, sellingPriceTTC: sourceLine.totalTTC };
          rows.push(renderLineRows([line], formatMoney));
        });
      walk(section.children, depth + 1, `${number}.`);
      if (!isDefaultQuoteLinesSection) rows.push(sectionSubtotal(section, formatMoney));
    });
  };
  walk(sections);
  return rows.join('');
};
const renderOptionBlocks = (options, formatMoney) => (options || []).map((option, optionIndex) => `
  <section class="quote-option-block">
    <div class="quote-option-title">
      <div><span>Option ${optionIndex + 1}</span><strong>${escapeHtml(option.title || "Option")}</strong></div>
      ${option.selected ? '<em>Option retenue</em>' : ''}
    </div>
    <table class="lines">
      <thead>
        <tr><th>Désignation</th><th style="width:62px;">Qté</th><th style="width:66px;">Unité</th><th style="width:92px;">Total HT</th><th>TVA</th><th>TTC</th></tr>
      </thead>
      <tbody>
        ${(option.lines || []).length ? (option.lines || []).map((line) => `
          <tr><td>${escapeHtml(line.description || line.label || "-")}</td><td class="center">${escapeHtml(formatQuantity(line.quantity))}</td><td class="center">${escapeHtml(line.unit || "")}</td><td class="right">${formatMoney(line.sellingPriceHT)}</td><td class="right">${formatMoney(line.vatAmount)}</td><td class="right">${formatMoney(line.sellingPriceTTC)}</td></tr>
        `).join("") : '<tr><td colspan="6">Aucune ligne visible.</td></tr>'}
      </tbody>
    </table>
    ${taxBreakdownHtml(option, formatMoney)}
    <div class="quote-option-totals">
      <span>HT <strong>${formatMoney(option.totals?.totalHT)}</strong></span>
      <span>TVA <strong>${formatMoney(option.totals?.totalVat)}</strong></span>
      <span>TTC <strong>${formatMoney(option.totals?.totalTTC)}</strong></span>
    </div>
  </section>
`).join("");

const renderContractModalities = (editor) => `
  <section class="contract-modalities">
    <h2>Modalités de la prestation</h2>
    <div><strong>Remise des livrables</strong><p>${escapeHtml(deliveryFormatLabel(editor.deliveryFormatCode, editor.deliveryFormatOther))}</p></div>
    <div><strong>Visites sur site</strong><p>${editor.siteVisitsIncluded ? `Oui${editor.siteVisitCount ? ` - ${escapeHtml(editor.siteVisitCount)} visite(s) prévue(s)` : ''}` : 'Non prévues'}</p>${editor.siteVisitsIncluded && editor.siteVisitConditions ? `<p>${escapeHtml(editor.siteVisitConditions).replace(/\n/g, '<br />')}</p>` : ''}</div>
  </section>
`;

const renderPaymentSchedule = (editor, formatMoney, terms) => {
  const depositRate = Number(editor.depositRate || 0);
  const schedule = clientPaymentScheduleItems(editor.paymentSchedule, depositRate);
  const rows = [
    ...(depositRate > 0 ? [`Acompte à la commande : ${formatPercentage(depositRate)} %`] : []),
    ...schedule.map((item) => formatPaymentScheduleItem(item, { formatMoney })),
  ];
  return rows.length ? `
    <section class="payment-schedule">
      <h2>Échéancier de paiement</h2>
      ${terms.commercialTerms.paymentScheduleNotice ? `<p>${escapeHtml(terms.commercialTerms.paymentScheduleNotice)}</p>` : ''}
      <table><tbody>${rows.map((label, index) => `<tr><td>${index + 1}</td><td colspan="2">${escapeHtml(label)}</td></tr>`).join('')}</tbody></table>
    </section>
  ` : '';
};

const QUOTE_LEGAL_FOOTER = "SAS Ingénierie Court-Circuit - Chez Art'Immo - Bâtiment La Géode - 10 Allée des Artilleurs - 42000 Saint-Etienne - SIRET 10657502000016 - TVA FR74106575020";

const renderQuotePageFooterContent = (legal = QUOTE_LEGAL_FOOTER) => `
    <div class="quote-page__footer-legal">${escapeHtml(legal)}</div>
    <div class="quote-page__footer-paraph">
      <div class="quote-page__footer-paraph-label">Paraphe du client</div>
      <div class="quote-page__footer-paraph-box"></div>
    </div>
`;

const renderQuotePageFooter = (legal) => `
  <footer class="quote-page__footer">
    ${renderQuotePageFooterContent(legal)}
  </footer>
`;

const renderQuoteFlowFooter = (legal) => `
  <tr>
    <td class="quote-flow__footer-cell">
      <footer class="quote-page__footer quote-page__footer--flow">
        ${renderQuotePageFooterContent(legal)}
      </footer>
    </td>
  </tr>
`;

const renderV2Sections = (sections, formatMoney, prefix = '', depth = 0) => sections.map((section, sectionIndex) => {
  const number = `${prefix}${sectionIndex + 1}`, numbers = new Map(), counts = new Map();
  return `
  ${sectionHeading(section, number, depth)}
  ${section.lines.map(line => {
    const parent = line.parentLineId || '', index = (counts.get(parent) || 0) + 1;
    counts.set(parent, index);
    const lineNumber = `${numbers.get(parent) || number}.${index}`;
    numbers.set(line.id, lineNumber);
    return `<tr class="composition-row ${line.structureKind === 'composite' ? 'composition-row--group' : ''}" data-line-id="${escapeHtml(line.id)}"><td class="line-number">${escapeHtml(lineNumber)}</td>
    <td style="padding-left:${8 + Math.min(line.depth, 8) * 10}px"><strong>${escapeHtml(formatCodeLabel(line.code, line.description))}</strong>
      ${line.includedInParent ? '<div class="line-comment">Inclus dans l’ensemble parent — non additionnel</div>' : ''}
      ${line.lineComment ? `<div class="line-comment">${escapeHtml(line.lineComment).replace(/\n/g, '<br />')}</div>` : ''}</td>
    <td class="center">${escapeHtml(formatQuantity(line.localQuantity))}</td>
    <td class="center">${escapeHtml(line.unit)}</td><td class="right">—</td>
    ${['totalHT', 'totalVat', 'totalTTC'].map(key => `<td class="right">${line.prices ? formatMoney(line.prices[key]) : '—'}</td>`).join('')}</tr>`;
  }).join('')}
  ${renderV2Sections(section.children, formatMoney, `${number}.`, depth + 1)}
  ${sectionSubtotal(section, formatMoney)}
`;
}).join('');

const renderV2Options = (options, formatMoney) => options.map(option => `
  <section class="quote-option-block"><div class="quote-option-title"><strong>${escapeHtml(option.title)}</strong><span>Option autonome — exclue du devis initial${option.selected ? ' — sélectionnée' : ''}</span></div>
  <table class="lines">${costingColumns}<tbody>${renderV2Sections(option.sections, formatMoney)}</tbody></table>
  ${taxBreakdownHtml(option, formatMoney)}<div class="quote-option-totals"><span>HT ${formatMoney(option.totalHT)}</span><span>TVA ${formatMoney(option.totalVat)}</span><span>TTC ${formatMoney(option.totalTTC)}</span></div></section>
`).join('');

const buildQuoteHtmlContent = ({ editor, calculated, project, affair, formatMoney, logoUrl, documentV2 }) => {
  const identity = documentV2?.company || companyDocumentIdentity;
  const footerLegal = documentV2?.company.footerLegalText;
  const commercial = commercialFields(editor);
  assertEuro(editor.currencyCode);
  if (calculated?.salesSheet?.currency !== undefined) assertEuro(calculated.salesSheet.currency);
  assertFinancialPresentation(calculated, editor.versionStatus && editor.versionStatus !== 'draft' ? editor.serverCalculation?.totals : null);
  const clientOptions = documentV2 ? documentV2.facts.options : clientOptionDocumentViews(editor.options, calculated.optionViews);
  const quoteDate = editor.createdAt || editor.created_at || new Date();
  const terms = documentTerms({ status: editor.versionStatus, clientDocument: editor.clientDocument, validUntil: editor.validUntil, validityDays: editor.validityDays,
    paymentTerms: commercial.paymentTerms, paymentSchedule: editor.paymentSchedule });
  const validityDateLabel = terms.validityDateLabel;
  const frozen = editor.versionStatus && editor.versionStatus !== 'draft';
  const clientProject = frozen ? null : project;
  const client = getQuoteClient(editor, clientProject);
  const affairStartDate = frozen ? '' : getValue(affair, 'desiredStartDate', 'desired_start_date');
  const affairExecutionDate = frozen ? '' : getValue(affair, 'desiredEndDate', 'desired_end_date');
  const quoteNumber = commercialReference(editor.quoteNumber, editor.versionNumber ?? 1) || "Devis provisoire";
  const clientRows = documentV2 ? renderV2Sections(documentV2.facts.sections, formatMoney) : renderHierarchicalRows(editor, calculated, formatMoney);
  const escalationFormula = quoteParameterText(editor.versionParameters, 'escalationFormula');
  const revisionFormula = quoteParameterText(editor.versionParameters, 'revisionFormula');
  const resolvedLogoUrl = documentV2?.company.logoDataUrl || quoteCoverLogo || logoUrl || companyDocumentIdentity.logoUrl;
  const logoSrc = resolvedLogoUrl ? new URL(resolvedLogoUrl, window.location.origin).href : "";

  return buildHtmlDocumentShell({
    title: quoteNumber,
    head: documentV2 ? '<meta name="quote-document-schema" content="2" /><meta name="quote-document-template" content="ICC-QUOTE-B5-1" />' : '',
    composition: DOCUMENT_COMPOSITIONS.COMMERCIAL,
    styles: `
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; }
    body { background: #fff; color: #202124; font-family: Arial, Helvetica, sans-serif; font-size: 12px; line-height: 1.35; margin: 0; }
    .quote-page { background: #fff; box-sizing: border-box; display: flex; flex-direction: column; min-height: 297mm; padding: 12mm 14mm 8mm; width: 210mm; break-after: page; page-break-after: always; overflow: visible; }
    .quote-page:last-child { break-after: auto; page-break-after: auto; }
    .quote-page__content { flex: 1 1 auto; min-height: 0; }
    .quote-page__footer { align-items: center; border-top: 1px solid #d9d9df; display: grid; flex: 0 0 23mm; gap: 8mm; grid-template-columns: minmax(0, 1fr) 34mm; margin-top: 4mm; padding-top: 2.5mm; width: 100%; }
    .quote-page__footer-legal { align-self: center; color: #4b5563; font-size: 6px; line-height: 1.2; overflow: hidden; text-align: left; text-overflow: clip; white-space: nowrap; width: 100%; }
    .quote-page__footer-paraph { align-self: end; justify-self: end; width: 34mm; }
    .quote-page__footer-paraph-label { font-size: 6.5px; line-height: 1; margin-bottom: 1.3mm; text-align: center; text-transform: uppercase; }
    .quote-page__footer-paraph-box { background: #fff; border: 1px solid #202124; height: 12mm; width: 34mm; }
    .quote-cover { display: flex; flex-direction: column; }
    .quote-cover__top { align-items: center; display: grid; gap: 18mm; grid-template-columns: minmax(0, 1fr) 66mm; min-height: 58mm; }
    .quote-cover__logo { align-items: center; display: flex; min-height: 55mm; }
    .quote-cover__logo img { display: block; height: auto; max-height: 54mm; max-width: 66mm; object-fit: contain; object-position: left center; width: auto; }
    .quote-cover__title { text-align: right; }
    .quote-cover__title h1 { font-size: 30px; margin: 0 0 8px; text-transform: uppercase; }
    .quote-cover__meta { border-collapse: collapse; margin-left: auto; }
    .quote-cover__meta td { padding: 2px 0 2px 10px; }
    .quote-cover__blocks { border-top: 2px solid #202124; display: grid; gap: 8mm; grid-template-columns: 1fr 1fr; margin-top: 8mm; padding-top: 8mm; }
    .quote-cover__block { border: 1px solid #d9d9df; border-radius: 6px; min-height: 48mm; padding: 7mm; }
    .quote-cover__block h2 { color: #5f6368; font-size: 11px; margin: 0 0 5mm; text-transform: uppercase; }
    .quote-cover__block strong { font-size: 15px; }
    .quote-cover__object { margin: 14mm auto 0; max-width: 150mm; text-align: center; overflow-wrap: anywhere; }
    .quote-cover__object h2 { color: #5f6368; font-size: 11px; margin: 0 0 4mm; text-transform: uppercase; }
    .quote-cover__object strong { display: block; font-size: 19px; line-height: 1.3; }
    .quote-cover__object-extra { color: #4b5563; font-size: 10px; margin-top: 5mm; }
    .quote-details-heading { align-items: baseline; border-bottom: 2px solid #202124; display: flex; justify-content: space-between; margin-bottom: 6mm; padding-bottom: 3mm; }
    .quote-details-heading strong { font-size: 13px; }
    .quote-details-heading span { color: #6b7280; font-size: 10px; }
    .header { display: grid; grid-template-columns: 1fr auto; gap: 24px; align-items: start; border-bottom: 2px solid #202124; padding-bottom: 18px; }
    .brand { display: grid; gap: 7px; justify-items: start; }
    .brand img { display: block; height: 34px; object-fit: contain; object-position: left center; width: auto; max-width: 118px; margin: 0 0 3px 0; }
    .company-block { display: grid; gap: 2px; }
    .company-block strong { font-size: 15px; }
    .company-block span { display: block; }
    .legal-lines { color: #5f6368; font-size: 10px; margin-top: 8px; }
    .quote-title { text-align: right; }
    .quote-title h1 { font-size: 26px; letter-spacing: 0; margin: 0 0 8px; text-transform: uppercase; }
    .quote-title table { border-collapse: collapse; margin-left: auto; }
    .quote-title td { padding: 2px 0 2px 12px; }
    .blocks { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin: 22px 0; }
    .block { border: 1px solid #d9d9df; border-radius: 6px; padding: 12px; }
    .block h2 { color: #5f6368; font-size: 11px; margin: 0 0 8px; text-transform: uppercase; }
    .block strong { font-size: 14px; }
    table.lines, table.totals, table.vat { border-collapse: collapse; width: 100%; }
    table.lines th { background: #f4f4f6; border: 1px solid #d9d9df; color: #4b5563; font-size: 10px; padding: 7px; text-transform: uppercase; }
    table.lines td { border: 1px solid #e6e7eb; padding: 8px; vertical-align: top; }
    .section-row td { background: #eef0f3; border-top: 2px solid #cfd3da !important; padding: 8px 10px !important; }
    .section-depth-1 td { padding-left: 18px !important; background: #f4f5f7; }
    .section-depth-2 td { padding-left: 28px !important; background: #f8f8fa; }
    .section-depth-3 td, .section-depth-4 td { padding-left: 38px !important; }
    .text-row td { font-style: italic; background: #fcfcfd; }
    .line-comment { color: #5f6368; font-size: 11px; margin-top: 4px; white-space: normal; }
    .right { text-align: right; }
    .center { text-align: center; }
    .quote-option-block { break-inside: avoid; margin-top: 18px; }
    .quote-option-title { align-items: center; background: #f4f4f6; border: 1px solid #d9d9df; border-bottom: 0; display: flex; justify-content: space-between; padding: 8px 10px; }
    .quote-option-title div { display: grid; gap: 2px; }
    .quote-option-title span { color: #6b7280; font-size: 9px; font-weight: 700; text-transform: uppercase; }
    .quote-option-title em { border: 1px solid #cbd5e1; border-radius: 999px; font-size: 9px; font-style: normal; padding: 3px 7px; }
    .quote-option-totals { display: flex; gap: 16px; justify-content: flex-end; padding: 8px 0; }
    .quote-option-totals span { font-size: 10px; }
    .summary { display: grid; grid-template-columns: minmax(220px, 1fr) 260px; gap: 22px; margin-top: 20px; align-items: start; }
    table.vat td, table.totals td { border-bottom: 1px solid #ececf0; padding: 6px 0; }
    table.totals tr:last-child td { border-bottom: 2px solid #202124; font-size: 15px; font-weight: 800; padding-top: 10px; }
    .quote-costing-print { color: #111b31; }
    .quote-costing-print table.lines { table-layout: fixed; }
    .quote-costing-print table.lines th { background: #fff; border: 0; border-bottom: 1px solid #9baec2; color: #405875; padding: 10px 5px; font-size: 9px; text-align: right; }
    .quote-costing-print table.lines th:nth-child(-n+4) { text-align: center; }
    .quote-costing-print table.lines th:nth-child(2) { text-align: left; }
    .quote-costing-print table.lines td { border: 0; border-bottom: 1px solid #dde4eb; padding: 10px 5px; vertical-align: top; overflow-wrap: anywhere; font-size: 10px; }
    .quote-costing-print table.lines td.right { white-space: nowrap; font-variant-numeric: tabular-nums; }
    .quote-costing-print table.lines td:last-child { font-weight: 700; }
    .quote-costing-print .composition-row strong { font-weight: 400; }
    .quote-costing-print .composition-row--group strong { font-weight: 700; }
    .quote-costing-print table.lines .section-row td { background: #fff; border-top: 1px solid #9baec2 !important; border-bottom: 1px solid #9baec2; padding: 11px 5px !important; font-size: 12px; text-transform: uppercase; }
    .quote-costing-print .section-number { font-weight: 800; color: #122b43; }
    .quote-costing-print .line-number { color: #405875; font-size: 9px; }
    .quote-costing-print table.lines .section-subtotal td { border-top: 1px solid #9baec2; border-bottom: 0; padding-top: 10px; padding-bottom: 15px; font-weight: 700; }
    .quote-costing-print .section-subtotal td:first-child { text-align: right; }
    .quote-costing-print .line-comment { font-size: 9px; color: #617084; font-weight: 400; }
    .quote-costing-print .summary { grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr); gap: 18px; align-items: start; margin-top: 16px; }
    .quote-costing-print .quote-vat-summary h2 { font-size: 11px; color: #122b43; margin: 0 0 8px; text-transform: uppercase; }
    .quote-costing-print table.vat { font-size: 9px; }
    .quote-costing-print table.vat th { text-align: right; font-weight: 400; color: #405875; padding: 7px 4px; border-top: 1px solid #9baec2; border-bottom: 1px solid #dde4eb; }
    .quote-costing-print table.vat td { padding: 8px 4px; text-align: right; white-space: nowrap; }
    .quote-costing-print table.vat :is(th, td):first-child { text-align: left; }
    .quote-costing-print table.vat tfoot { background: transparent; font-weight: 700; }
    .quote-costing-print table.totals { background: transparent; border-collapse: separate; border-spacing: 0; border-left: 1px solid #ccd6e1; padding: 0 0 0 18px; border-radius: 0; font-size: 11px; }
    .quote-costing-print table.totals td { padding: 9px 5px; border-bottom-color: #ccd6e1; }
    .quote-costing-print table.totals tr:last-child td { background: transparent; color: #122b43; padding: 12px 8px; border-top: 1px solid #122b43; border-bottom: 0; font-size: 17px; }
    .document-notes { background: #f8f8fa; border: 1px solid #e6e7eb; border-radius: 6px; display: grid; gap: 10px; margin: 0 0 18px; padding: 12px; }
    .document-notes strong { font-size: 11px; text-transform: uppercase; }
    .document-notes p { margin: 4px 0 0; }
    .contract-modalities { background: #f0f4f7; color: #122b43; border: 1px solid #e3ebf2; border-radius: 6px; display: grid; gap: 8px; grid-template-columns: 1fr 1fr; margin: 0 0 6mm; padding: 12px; }
    .contract-modalities > div + div { border-left: 1px solid #ccd6e1; padding-left: 22px; }
    .contract-modalities h2 { font-size: 12px; grid-column: 1 / -1; margin: 0; text-transform: uppercase; }
    .contract-modalities strong { display: block; font-size: 10px; text-transform: uppercase; }
    .contract-modalities p { margin: 3px 0 0; }
    .payment-schedule { break-inside: avoid; margin-top: 6mm; }
    .payment-schedule h2 { font-size: 12px; text-transform: uppercase; }
    .payment-schedule table { border-collapse: collapse; width: 100%; }
    .payment-schedule td { border-bottom: 1px solid #e6e7eb; padding: 6px; vertical-align: top; }
    .payment-schedule td:first-child { width: 28px; }
    .payment-schedule td:nth-child(2) { width: 95px; }
    .commercial-acceptance { display: grid; grid-template-columns: minmax(0, 1fr) 250px; gap: 18px; margin-top: 0; align-items: stretch; }
    .commercial-summary { border: 1px solid #d9d9df; border-radius: 6px; padding: 12px; }
    .commercial-summary h2 { font-size: 12px; margin: 0 0 10px; text-transform: uppercase; }
    .commercial-summary-grid { display: grid; grid-template-columns: 1fr; gap: 8px; }
    .commercial-summary-item { border-top: 1px solid #ececf0; padding-top: 7px; }
    .commercial-summary-item strong { display: block; font-size: 10px; margin-bottom: 3px; text-transform: uppercase; }
    .commercial-summary-item p { margin: 0; }
    .terms { border-top: 1px solid #d9d9df; color: #3f444c; font-size: 9.5px; line-height: 1.25; margin-top: 0; padding-top: 6px; }
    .terms--continued { border-top: 0; padding-top: 0; }
    .terms h3 { font-size: 10.8px; margin: 6px 0 3px; }
    .terms p { margin: 0 0 5px; }
    .terms ul { margin: 3px 0 5px 18px; padding: 0; }
    .terms li { margin: 1px 0; }
    .terms h2 { font-size: 12px; margin: 0 0 8px; text-transform: uppercase; }
    .signature-box { border: 1px solid #d9d9df; border-radius: 6px; min-height: 100%; padding: 12px; }
    .signature-box--client { min-height: 176px; }
    .muted { color: #6b7280; }
    .quote-flow { border-collapse: collapse; width: 210mm; }
    .quote-flow--details { break-before: page; page-break-before: always; }
    .quote-flow--terms { break-before: page; page-break-before: always; }
    .quote-flow thead { display: table-header-group; }
    .quote-flow tfoot { display: table-footer-group; }
    .quote-flow__top-spacer { height: 10mm; padding: 0; }
    .quote-flow__content-cell { padding: 0 14mm 3mm; vertical-align: top; }
    .quote-flow__footer-cell { height: 27mm; padding: 0 14mm 8mm; vertical-align: bottom; }
    .quote-page__footer--flow { height: 23mm; margin-top: 0; }
    .quote-flow tr, .quote-flow td { break-inside: auto; page-break-inside: auto; }
    .quote-flow table.lines tr, .quote-flow .summary, .quote-flow .commercial-acceptance, .quote-flow .commercial-summary-item, .quote-flow .signature-box, .quote-flow .terms h3 { break-inside: avoid; page-break-inside: avoid; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .quote-page { margin: 0; }
      .commercial-acceptance { margin-bottom: 0; }
    }
  `,
    body: `
  <section class="quote-page quote-page--cover">
    <div class="quote-page__content">
      <div class="quote-cover">
        <div class="quote-cover__top">
          <div class="quote-cover__logo">
            ${logoSrc ? `<img src="${escapeHtml(logoSrc)}" alt="${escapeHtml(identity.displayName || identity.legalName || "Entreprise")}" />` : ""}
          </div>
          <div class="quote-cover__title">
            <h1>Devis</h1>
            <table class="quote-cover__meta">
              <tr><td class="muted">Numéro</td><td><strong>${escapeHtml(quoteNumber)}</strong></td></tr>
              <tr><td class="muted">Version</td><td>${escapeHtml(editor.versionNumber ?? 1)}</td></tr>
              <tr><td class="muted">Date</td><td>${escapeHtml(documentV2 ? documentV2.identity.createdAtLabel : formatDate(quoteDate))}</td></tr>
              <tr><td class="muted">Validité</td><td>${escapeHtml(validityDateLabel)}</td></tr>
            </table>
          </div>
        </div>

        <section class="quote-cover__blocks">
          <div class="quote-cover__block">
            <h2>Notre société</h2>
            <strong>${escapeHtml(identity.legalName || identity.displayName || 'Ingénierie Court-Circuit')}</strong><br />
            ${(identity.addressLines || []).map((line) => `${escapeHtml(line)}<br />`).join('')}
            ${(identity.legalLines || []).map((line) => `<span class="muted">${escapeHtml(line)}</span><br />`).join('')}
          </div>
          <div class="quote-cover__block">
            <h2>Client</h2>
            <strong>${escapeHtml(getClientName(client, clientProject))}</strong><br />
            ${getClientAddressHtml(client, clientProject)}
            ${getValue(client, "email") ? `<br />${escapeHtml(getValue(client, "email"))}` : ""}
            ${getValue(client, "phone") ? `<br />${escapeHtml(getValue(client, "phone"))}` : ""}
            ${getValue(client, "contactName") ? `<br /><span class="muted">À l’attention de ${escapeHtml(getValue(client, "contactName"))}</span>` : ""}
          </div>
        </section>

        ${(String(commercial.objectText || '').trim() || commercial.scopeText || commercial.exclusionsText || commercial.notesClient) ? `<section class="quote-cover__object">
          ${String(commercial.objectText || '').trim() ? `<h2>Objet du devis</h2><strong>${escapeHtml(String(commercial.objectText).trim())}</strong>` : ''}
          ${(commercial.scopeText || commercial.exclusionsText || commercial.notesClient) ? `<div class="quote-cover__object-extra">${commercial.scopeText ? `<div><strong style="font-size:10px;">Périmètre</strong><br />${escapeHtml(commercial.scopeText).replace(/\n/g, '<br />')}</div>` : ''}${commercial.exclusionsText ? `<div style="margin-top:3mm;"><strong style="font-size:10px;">Exclusions</strong><br />${escapeHtml(commercial.exclusionsText).replace(/\n/g, '<br />')}</div>` : ''}${commercial.notesClient ? `<div style="margin-top:3mm;"><strong style="font-size:10px;">Note au client</strong><br />${escapeHtml(commercial.notesClient).replace(/\n/g, '<br />')}</div>` : ''}</div>` : ''}
        </section>` : ''}
      </div>
    </div>
    ${renderQuotePageFooter(footerLegal)}
  </section>

  <table class="quote-flow quote-flow--details">
    <thead>
      <tr><td class="quote-flow__top-spacer"></td></tr>
    </thead>
    <tfoot>
      ${renderQuoteFlowFooter(footerLegal)}
    </tfoot>
    <tbody>
      <tr>
        <td class="quote-flow__content-cell">

      ${renderContractModalities(editor)}

      <div class="quote-costing-print">
      <table class="lines">
        ${costingColumns}
        <thead>
          <tr>
            <th>#</th>
            <th>Désignation</th>
            <th>Qté</th>
            <th>Unité</th>
            <th>Prix unitaire HT</th>
            <th>Total HT</th>
            <th>Montant TVA</th>
            <th>Total TTC</th>
          </tr>
        </thead>
        <tbody>
          ${clientRows || '<tr><td colspan="8">Aucune ligne visible client.</td></tr>'}
        </tbody>
      </table>

      ${clientOptions.length ? `<section class="options-section"><h2>Options</h2><p class="muted">Les options sont chiffrées séparément et ne sont pas intégrées au total du devis initial.</p>${documentV2 ? renderV2Options(clientOptions, formatMoney) : renderOptionBlocks(clientOptions, formatMoney)}</section>` : ""}
      ${calculated.optionTotalsUnavailable ? '<p>Totaux des options non disponibles dans la version enregistrée.</p>' : ''}

      <section class="summary" style="break-inside:avoid">
        <div class="quote-vat-summary"><h2>Détail de la TVA</h2>${costingTaxTable(calculated, formatMoney)}</div>
        <table class="totals"><tbody>
          <tr><td>Total HT</td><td class="right">${formatMoney(calculated.totals.totalHT)}</td></tr>
          <tr><td>Montant TVA</td><td class="right">${formatMoney(calculated.totals.totalVat)}</td></tr>
          <tr><td>Total TTC</td><td class="right">${formatMoney(calculated.totals.totalTTC)}</td></tr>
        </tbody></table>
      </section>
      </div>

      ${renderPaymentSchedule(editor, formatMoney, terms)}

      <section class="commercial-acceptance" style="margin-top:8mm;">
        <div class="commercial-summary">
          <h2>Conditions commerciales</h2>
          <div class="commercial-summary-grid">
            <div class="commercial-summary-item"><strong>Validité du devis</strong><p>${editor.versionStatus === 'draft' ? escapeHtml(validityDateLabel) : `${escapeHtml(editor.validityDays ?? 30)} jours - jusqu’au ${escapeHtml(validityDateLabel)}`}</p></div>
            <div class="commercial-summary-item"><strong>Conditions de paiement</strong><p>${escapeHtml(terms.commercialTerms.paymentTerms)}</p></div>
            ${commercial.executionDelay ? `<div class="commercial-summary-item"><strong>Délai d’exécution</strong><p>${escapeHtml(commercial.executionDelay).replace(/\n/g, '<br />')}</p></div>` : ''}
            ${(affairStartDate || affairExecutionDate) ? `<div class="commercial-summary-item"><strong>Calendrier prévisionnel</strong>${affairStartDate ? `<p>Démarrage : ${escapeHtml(formatDate(affairStartDate))}</p>` : ''}${affairExecutionDate ? `<p>Exécution prévue : ${escapeHtml(formatDate(affairExecutionDate))}</p>` : ''}</div>` : ''}
            ${escalationFormula ? `<div class="commercial-summary-item"><strong>Formule d’actualisation</strong><p>${escapeHtml(escalationFormula).replace(/\n/g, '<br />')}</p></div>` : ''}
            ${revisionFormula ? `<div class="commercial-summary-item"><strong>Formule de révision</strong><p>${escapeHtml(revisionFormula).replace(/\n/g, '<br />')}</p></div>` : ''}
            <div class="commercial-summary-item"><strong>Prévention des risques</strong><p>${escapeHtml(terms.commercialTerms.riskPreventionText)}</p></div>
          </div>
        </div>
        <div class="signature-box signature-box--client">
          <strong>Bon pour accord client</strong><br />
          <span class="muted">Date, nom, qualité, signature et cachet</span><br /><br />
          <span>La signature du devis vaut acceptation du devis et des conditions et mentions qui y sont annexées.</span>
        </div>
      </section>
        </td>
      </tr>
    </tbody>
  </table>

  <table class="quote-flow quote-flow--terms">
    <thead>
      <tr><td class="quote-flow__top-spacer"></td></tr>
    </thead>
    <tfoot>
      ${renderQuoteFlowFooter(footerLegal)}
    </tfoot>
    <tbody>
      <tr>
        <td class="quote-flow__content-cell">
          <section class="terms">
${terms.blocks.map((block) => {
  const tag = block.kind === 'title' ? 'h2' : block.kind === 'heading' ? 'h3' : 'p';
  return `<${tag}>${block.kind === 'item' ? '&#8226; ' : ''}${escapeHtml(block.text)}</${tag}>`;
}).join('\n')}
          </section>
        </td>
      </tr>
    </tbody>
  </table>

`,
  });
};

export const buildQuoteHtml = (context) => {
  if (context.editor?.contentSchemaVersion === 2) throw Object.assign(new Error('Une projection documentaire v2 vérifiée est requise.'), { code: 'QUOTE_DOCUMENT_V2_UNVERIFIED' });
  return buildQuoteHtmlContent({ ...context, documentV2: undefined });
};

// Only verified client projections enter the shared preview/print template.
export const buildQuoteSnapshotHtmlV2 = ({ document, formatMoney }) => {
  const context = documentV2RenderContext(document);
  return buildQuoteHtmlContent({ ...context, documentV2: context.document, formatMoney });
};

const openQuoteDocumentWindow = async ({ editor, calculated, project, affair, formatMoney, logoUrl, calculationStatus, print = false }) => {
  assertQuoteDocumentReady({ editor, calculated, calculationStatus });
  if (editor.contentSchemaVersion === 2) {
    const target = window.open('', '_blank', 'width=1024,height=768');
    if (!target) throw new Error('Autorisez l’ouverture de la fenêtre documentaire.');
    target.opener = null;
    try {
      const { document } = await previewQuoteDocument(editor.id, editor.quoteVersionId, 'client_quote');
      return await openHtmlDocument({ html: buildQuoteSnapshotHtmlV2({ document, formatMoney }), print, waitForResources: true, targetWindow: target });
    } catch (error) { target.close(); throw error; }
  }
  return openHtmlDocument({
    html: buildQuoteHtml({ editor, calculated, project, affair, formatMoney, logoUrl }),
    print,
    waitForResources: true,
  });
};

export const previewQuotePdf = (context) => openQuoteDocumentWindow({ ...context, print: false });
export const exportQuotePdf = (context) => openQuoteDocumentWindow({ ...context, print: true });
